import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { RequestHandlerScreen } from 'common-src/components/base';
import { getCurrentUserId, getRoleConfig } from 'common-src/features/auth';
import { getIsClientsCached } from 'common-src/features/cache';
import { deleteOrmItem } from 'common-src/features/rest';
import AwscChatMessage, {
  AwscChatMessageStatus,
  deleteAwscChatMessage,
  getAwscChatMessageRequest,
  getScheduledAwscChatMessages,
} from 'common-src/models/AwscChatMessage';

import { ModalType } from 'src/components/base/ModalGroup';
import {
  ActionsRow,
  BaseTable,
  MultilineTextItem,
  TablePagination,
  TextItem,
} from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import { TableHeaders } from './constants';

const ScheduledMessagesScreen = () => {
  const dispatch = useDispatch();

  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });

  const scheduledChatMessages = useCustomSelector((state) => getScheduledAwscChatMessages(state));
  const isClientsCached = useCustomSelector((state) => getIsClientsCached(state));
  const currentUserId = useCustomSelector((state) => getCurrentUserId(state));
  const permission = useCustomSelector((state) => getRoleConfig(state));
  const canManageScheduledMessages = permission?.canManageScheduledMessages;

  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading(
    [
      getAwscChatMessageRequest({
        status: AwscChatMessageStatus.Scheduled,
        client_id: canManageScheduledMessages ? undefined : currentUserId,
      }),
    ],
    () => {},
    !isClientsCached,
  );

  useInterval(() => {
    dispatch(
      getAwscChatMessageRequest({
        status: AwscChatMessageStatus.Scheduled,
        client_id: canManageScheduledMessages ? undefined : currentUserId,
        sort_order: 'desc',
        limit: 5,
      }),
    );
  });

  const getData = () =>
    scheduledChatMessages.map((chatMessage) => ({
      clientName: (
        <TextItem isActive text={chatMessage.client ? chatMessage.client.getName() : '-'} />
      ),
      patientName: (
        <TextItem isActive text={chatMessage.patient ? chatMessage.patient.getName() : '-'} />
      ),
      message: <MultilineTextItem isActive text={chatMessage.message} maxLines={6} />,
      scheduledAt: (
        <TextItem
          isActive
          text={moment(chatMessage.scheduledAt)
            .tz(chatMessage.patient?.timeZone)
            .format('MM/DD/YYYY hh:mm A')}
          value={chatMessage.scheduledAt}
        />
      ),
      createdAt: (
        <TextItem
          isActive
          text={moment(chatMessage.createdAt).format('MM/DD/YYYY')}
          value={chatMessage.createdAt}
        />
      ),
      actions: (
        <ActionsRow
          options={[
            {
              id: 'edit-button',
              text: 'Edit',
              backgroundColor: '#555671B2',
              color: 'white',
              disabled:
                currentUserId === chatMessage.client?.id ? false : !canManageScheduledMessages,
              onClick: openModal(ModalType.SCHEDULE_SMS_MESSAGE, {
                patientId: chatMessage.patient?.id,
                id: chatMessage.id,
              }),
            },
            {
              id: 'remove-button',
              text: 'Remove',
              backgroundColor: '#F01F51B2',
              color: 'white',
              disabled:
                currentUserId === chatMessage.client?.id ? false : !canManageScheduledMessages,
              onClick: deleteAwscChatMessage(chatMessage.id, {
                successBlock: () => {
                  deleteOrmItem(AwscChatMessage.modelName, chatMessage.id);
                  showAlert(AlertType.Success, '', 'You have successfully deleted the message');
                },
              }),
              showWarning: true,
            },
          ]}
        />
      ),
    }));

  if (isRequesting || !isClientsCached) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling scheduled messages..." />;
  }

  return (
    <>
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="calls"
        classNames={[
          'medical-table-new',
          scheduledChatMessages?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No scheduled messages"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'createdAt',
          label: 'Created At',
          width: '10%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={scheduledChatMessages} />
    </>
  );
};

export default ScheduledMessagesScreen;
